import { Component, OnInit, Input, EventEmitter, Output, signal, TemplateRef, ViewChild, ElementRef, NO_ERRORS_SCHEMA } from '@angular/core';
import { FlowObjectType, FlowObjectDefinition, FormSchema, PairValueTypeValues } from '../../../../models/flow-object.model';
import { AuthenticationModel, AuthenticationSchemeType, AuthenticationSchemeTypeDescription, ConfigSchema, HttpMethod, HttpMethodDescription } from '../../../../models/config-schema.model';
import { FlowDefinition } from '../../../../models/flow.model';
import { ToastrService } from 'ngx-toastr';
import { Enums } from '../../../../shared/enums';
import { FOD_MIDWAY_OUTBOUND_API_TINYMCE_OPTIONS } from './tinymce-options';
import { Utils } from '../../../../shared/utils';
import { IBaseOption } from '../../../../models/base.model';
import { FlowObjectDefinitionService } from '../../../../services/flow-object-definition.service';
import { IMPORTS } from './_imports';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

@Component({
    selector: 'fod-midway-outbound-api',
    templateUrl: './fod-midway-outbound-api.component.html',
    styleUrl: './fod-midway-outbound-api.component.scss',
    standalone: true,
    imports: IMPORTS,
    schemas: [NO_ERRORS_SCHEMA]
})
export class FlowObjectDetailsMidwayOutboundApiComponent implements OnInit {
    // #region [ViewChild]
    @ViewChild('configSchemaRef') configSchemaRef: TemplateRef<HTMLElement>;
    @ViewChild('configSchemaEditorRef') configSchemaEditorRef: ElementRef;
    // #endregion

    // #region [Type props]
    Utils = Utils;
    Enums = Enums;
    AuthenticationSchemeType = AuthenticationSchemeType;
    HttpMethod = HttpMethod;
    PairValueTypeValues = PairValueTypeValues;
    // #endregion

    // #region [props]
    configSchema = new ConfigSchema();
    fullJson = '';
    keyValuePairsObject: any = {};
    tinyMceOptions: any = FOD_MIDWAY_OUTBOUND_API_TINYMCE_OPTIONS;
    previousShowJsonView = false;
    httpMethodOptions: IBaseOption[] = [];
    authenticationSchemeOptions: IBaseOption[] = [];
    openTabs: number[] = [1];
    configSchemaDialog: MatDialogRef<HTMLElement, any>;
    // #endregion

    // #region [signals]
    $model = signal(null as FlowObjectDefinition);
    $isReadOnlyMode = signal(false);
    $isCreate = signal(false);
    $url = signal('');
    $pairKey = signal('');
    $pairValue = signal('');
    $pairValueType = signal(null as string);
    $jsonToEdit = signal('');
    $publicSystemName = signal('');
    $keyValuePairsArray = signal([] as any[]);
    $formFlowObjectEntries = signal([] as any[]);
    $formFlowObject = signal(null as FlowObjectDefinition);
    $showJsonView = signal(false);
    $authentication = signal(new AuthenticationModel());
    $ignoreErrors = signal(false);
    $errorMessage = signal(null as string);
    $showParametersUrl = signal(false);
    $parameterKey = signal(null as string);
    $parameterKeyType = signal(null as string);
    $keyValueParametersArray = signal([] as any[]);
    $useUrlParameters = signal(false);
    $selectedHttpMethod = signal(HttpMethod.Get);
    $configSchemaFlat = signal(null as string);
    // #endregion

    // #region [Input/Output]
    @Input() _model: FlowObjectDefinition;
    @Input() _flowDefinition: FlowDefinition;
    @Input() _isReadOnlyMode: boolean;
    @Input() _isCreate: boolean;
    @Output() _submitEvent = new EventEmitter<FlowObjectDefinition>();
    @Output() _closeEvent = new EventEmitter<any>();
    // #endregion

    constructor(
        private dialog: MatDialog,
        private toastr: ToastrService,
        private flowObjectDefinitionService: FlowObjectDefinitionService,
    ) {
        for (let item in AuthenticationSchemeType) {
            if (!Number.isNaN(Number.parseInt(item))) {
                this.authenticationSchemeOptions.push({
                    value: +item,
                    description: AuthenticationSchemeTypeDescription.get(+item)
                });
            }
        }

        for (let item in HttpMethod) {
            if (!Number.isNaN(Number.parseInt(item))) {
                this.httpMethodOptions.push({
                    value: +item,
                    description: HttpMethodDescription.get(+item)
                });
            }
        }
    }

    async ngOnInit() {
        if (this.$model()?.configSchema != null) {
            this.configSchema = JSON.parse(this.$model().configSchema) as ConfigSchema;
            this.initFromModel();
        }

        this.$pairValueType.set(this.PairValueTypeValues.String);
        this.$parameterKeyType.set(this.PairValueTypeValues.AcessoCidadaoIdUsuarioSolicitante);

        if (!this.$isCreate()) {
            Utils.setSignal(this.$formFlowObject, this._flowDefinition.flowObjectDefinitions.find(x => x.typeId == FlowObjectType.StartForm));
            if (this.$formFlowObject() != null) {
                if (Utils.isNullOrEmpty(this.$formFlowObject().formSchema)) {
                    const response = await this.flowObjectDefinitionService.getFormSchema(this.$formFlowObject().id);

                    if (!response.isSuccess) {
                        this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                        return;
                    }

                    Utils.updSignal(this.$formFlowObject, x => {
                        x.formSchema = response.data;
                        return x;
                    });
                }

                Utils.setSignal(this.$formFlowObjectEntries, this.getFormFlowObjectEntries(this.$formFlowObject()));
            }
        }
    }

    ngOnChanges() {
        this.$model.set(this._model);
        this.$isReadOnlyMode.set(this._isReadOnlyMode);
        this.$isCreate.set(this._isCreate);
    }

    addKeyValuePair() {
        if (this.isPairValueTypeEmpty()) return;
        if (this.isPairKeyEmpty()) return;
        if (this.isPairKeyAlreadyAdded()) return;
        if (!this.isPairValueValid()) return;

        let keyValuePair = this.parseKeyValuePair();
        Utils.updSignal(this.$keyValuePairsArray, x => {
            x.push(keyValuePair);
            return x;
        });
        this.keyValuePairsObject = Object.assign(this.keyValuePairsObject, keyValuePair);
        this.fullJson = JSON.stringify(this.keyValuePairsObject);
        this.setJsonToEdit();
        this.clearKeyValueFields();
    }

    removeKeyValuePair(pair) {
        this.$keyValuePairsArray.set(this.$keyValuePairsArray().filter(x => x != pair));

        this.keyValuePairsObject = {};
        this.$keyValuePairsArray().forEach(item => {
            this.keyValuePairsObject = Object.assign(this.keyValuePairsObject, item);
        });

        this.fullJson = JSON.stringify(this.keyValuePairsObject);
        this.setJsonToEdit();
    }

    isJsonValid(event?: any) {
        if (event != null) {
            this.$showJsonView.set(event.value);
        }

        try {
            this.fullJson = this.getJsonToEditText();
            if (Utils.isNullOrEmpty(this.fullJson)) return true;

            this.keyValuePairsObject = JSON.parse(this.fullJson);
            this.$keyValuePairsArray.set([]);
            for (const key in this.keyValuePairsObject) {
                let value = this.keyValuePairsObject[key];
                if (!this.isReallyNumber(value) && !this.isBool(value) && !this.isNull(value)) {
                    value = `"${value}"`;
                }

                let keyValuePair = JSON.parse(`{"${key}": ${value}}`);
                Utils.updSignal(this.$keyValuePairsArray, x => {
                    x.push(keyValuePair);
                    return x;
                });
            }

            if (this.$showJsonView()) {
                this.setJsonToEdit();
                this.$jsonToEdit.set(Utils.jsonSyntaxHighlight(this.$jsonToEdit(), true));
            }

            this.previousShowJsonView = this.$showJsonView();
            return true;
        } catch (error) {
            this.toastr.error(Enums.Messages.JsonDeserializeError.toString(), Enums.Messages.Error.toString(), this.getToastrErrorOptions());
            console.error(error);
            this.$showJsonView.set(this.previousShowJsonView);
            if (event?.source != null) {
                event.source.checked = this.previousShowJsonView;
            }
            return false;
        }
    }

    setShowJsonView(value: boolean) {
        this.$showJsonView.set(value);
        this.isJsonValid();
    }

    getFormFieldKey(): string {
        return this.$pairValueType()
            .split(Enums.Messages.FormTypeStartTag).join('')
            .split(Enums.Messages.FormTypeEndTag).join('')
            .split(Enums.Messages.FormTypeSeparatorTag)
            .slice(-1)[0];
    }

    prettyPrint(pair): string {
        const jsonString = JSON.stringify(pair, null, 2);
        return Utils.jsonSyntaxHighlight(jsonString);
    }

    clearKeyValueFields(event?) {
        if (event != null) {
            this.$pairValueType.set(event);
        }

        this.$pairKey.set(null);
        this.$pairValue.set(null);
    }

    authenticationSchemeChangeHandler(event) {
        Utils.updSignal(this.$authentication, x => {
            x.authenticationScheme = event;
            return x;
        });

        if (this.$authentication().authenticationScheme != AuthenticationSchemeType.Jwt) {
            Utils.updSignal(this.$authentication, x => {
                x.clientId = null;
                x.clientSecret = null;
                x.scopes = null;
                x.tokenEndpoint = null;
                return x;
            });
        }

        if (
            this.$authentication().authenticationScheme == AuthenticationSchemeType.None
            || this.$authentication().authenticationScheme == AuthenticationSchemeType.Jwt
        ) {
            Utils.updSignal(this.$authentication, x => {
                x.basicOrApiKey = null;
                return x;
            });
        }
    }

    basicOrApiKeyChange(event) {
        Utils.updSignal(this.$authentication, x => {
            x.basicOrApiKey = event;
            return x;
        });
    }

    clientIdChange(event) {
        Utils.updSignal(this.$authentication, x => {
            x.clientId = event;
            return x;
        });
    }

    clientSecretChange(event) {
        Utils.updSignal(this.$authentication, x => {
            x.clientSecret = event;
            return x;
        });
    }

    scopesChange(event) {
        Utils.updSignal(this.$authentication, x => {
            x.scopes = event;
            return x;
        });
    }

    tokenEndpointChange(event) {
        Utils.updSignal(this.$authentication, x => {
            x.tokenEndpoint = event;
            return x;
        });
    }

    shouldDisablePairValueInput(): boolean {
        return this.$pairValueType() == this.PairValueTypeValues.EDocsEnc
            || this.$pairValueType() == this.PairValueTypeValues.EDocsProc
            || this.$pairValueType() == this.PairValueTypeValues.SendDateTime
            || this.$pairValueType() == this.PairValueTypeValues.AcessoCidadaoIdUsuarioSolicitante
            || this.$pairValueType() == this.PairValueTypeValues.AcessoCidadaoIdUsuarioExecutor
            || this.$pairValueType() == this.PairValueTypeValues.Null
            || this.isFormPairValueType();
    }

    toggleShowParametersUrl() {
        this.$showParametersUrl.set(!this.$showParametersUrl());

        if (!this.$showParametersUrl()) {
            Utils.updSignal(this.$keyValueParametersArray, x => {
                x.length = 0;
                return x;
            });
        }
    }

    addParameter() {
        if (this.isInvalidKey(this.$parameterKeyType(), this.$parameterKey())) return;

        const keyValue = this.parseKeyValuePair(this.$parameterKey(), this.$parameterKeyType());
        Utils.updSignal(this.$keyValueParametersArray, x => {
            x.push(keyValue);
            return x;
        });
        this.clearKeyValueFields();
    }

    removeParameter(parameter) {
        this.$keyValueParametersArray.set(this.$keyValueParametersArray().filter(x => x !== parameter));
    }

    toggleTab(tab: number) {
        if (this.isTabOpen(tab)) {
            this.openTabs = this.openTabs.filter(x => x != tab);
        } else {
            this.openTabs.push(tab);
        }
    }

    isTabOpen(tab: number): boolean {
        return this.openTabs.includes(tab);
    }

    showConfigSchema() {
        let tempObject = {
            model: JSON.parse(JSON.stringify(this.$model())) as FlowObjectDefinition,
            configSchema: JSON.parse(JSON.stringify(this.configSchema))
        };
        delete tempObject.model.configSchema;

        this.$configSchemaFlat.set(btoa(encodeURIComponent(JSON.stringify(tempObject))));

        this.configSchemaDialog = this.dialog.open(this.configSchemaRef, {
            minWidth: '800px'
        });

        this.configSchemaDialog.afterOpened().subscribe(() => {
            setTimeout(() => {
                this.configSchemaEditorRef.nativeElement.scrollLeft = 0;
                this.configSchemaEditorRef.nativeElement.scrollTop = 0;
            }, 1);
            setTimeout(() => {
                this.configSchemaEditorRef.nativeElement.style.opacity = '1';
            }, 100);
        });

        this.configSchemaDialog.afterClosed().subscribe(async () => {
            if (this.$isReadOnlyMode()) return;

            try {
                let modalObject = JSON.parse(decodeURIComponent(atob(this.configSchemaEditorRef.nativeElement.value)));
                delete modalObject.model.configSchema;

                // checa se o tipo da origem do conteúdo é o mesmo tipo do conteúdo atual e se a origem não é um FlowDefinition
                if (modalObject.model.typeId != this.$model().typeId || modalObject.model.targetId != null) {
                    this.toastr.error(Enums.Messages.InvalidConfigSchemaSourceType, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                // checa se o código-fonte foi alterado
                if (JSON.stringify(tempObject) != JSON.stringify(modalObject)) {
                    this.configSchema = JSON.parse(JSON.stringify(modalObject.configSchema)) as ConfigSchema;
                    this.initFromModel();
                }
            } catch (error) {
                this.toastr.error(Enums.Messages.InvalidFlowObjectConfigSchemaSource, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }
        });
    }

    closeForm() {
        this._closeEvent.emit();
    }

    onSubmit() {
        if (this.$isReadOnlyMode() || !this.isJsonValid()) return false;

        this.configSchema.taskOutboundApi.httpMethod = this.$selectedHttpMethod();
        this.configSchema.taskOutboundApi.url = this.$url();
        this.configSchema.taskOutboundApi.publicSystemName = this.$publicSystemName();
        this.configSchema.taskOutboundApi.ignoreErrors = this.$ignoreErrors();
        this.configSchema.taskOutboundApi.errorMessage = this.$errorMessage();

        this.configSchema.taskOutboundApi.urlParameters = {};
        if (this.$keyValueParametersArray().length > 0) {
            let objectsArray = {};
            this.$keyValueParametersArray().forEach(item => {
                objectsArray = Object.assign(objectsArray, item);
            });

            this.configSchema.taskOutboundApi.urlParameters = objectsArray;
        }

        if (this.configSchema.taskOutboundApi.authentication != null) {
            Object.assign(this.configSchema.taskOutboundApi.authentication, this.$authentication());
        } else {
            this.configSchema.taskOutboundApi.authentication = this.$authentication();
        }

        if (Utils.isNotNullOrEmpty(this.fullJson)) {
            this.configSchema.taskOutboundApi.data = JSON.parse(this.fullJson);
        }

        Utils.updSignal(this.$model, x => {
            x.configSchema = JSON.stringify(this.configSchema);
            return x;
        });

        this._submitEvent.emit(this.$model());
    }

    // #region [===== private methods =====]
    // #endregion

    private initFromModel() {
        // Configurações Básicas
        this.$selectedHttpMethod.set(this.configSchema.taskOutboundApi.httpMethod ?? HttpMethod.Get);
        this.$url.set(this.configSchema.taskOutboundApi.url);
        this.$publicSystemName.set(this.configSchema.taskOutboundApi.publicSystemName);
        this.$errorMessage.set(this.configSchema.taskOutboundApi.errorMessage);
        this.$ignoreErrors.set(this.configSchema.taskOutboundApi.ignoreErrors);

        for (const [key, value] of Object.entries(this.configSchema.taskOutboundApi.urlParameters)) {
            const keyValuePair = JSON.parse(`{"${key}": "${value}"}`);
            Utils.updSignal(this.$keyValueParametersArray, x => {
                x.push(keyValuePair);
                return x;
            });
        }

        if (this.$keyValueParametersArray().length > 0) {
            this.$showParametersUrl.set(true);
            this.$useUrlParameters.set(true);
        }

        // Autenticação
        Utils.setSignal(this.$authentication, this.configSchema.taskOutboundApi.authentication);

        // Corpo da Requisição
        this.fullJson = JSON.stringify(this.configSchema.taskOutboundApi.data);
        this.setJsonToEdit();

        for (const key in this.configSchema.taskOutboundApi.data) {
            let value = this.configSchema.taskOutboundApi.data[key];
            if (!this.isReallyNumber(value) && !this.isBool(value) && !this.isNull(value)) {
                value = `"${value}"`;
            }

            const keyValuePair = JSON.parse(`{"${key}": ${value}}`);
            this.keyValuePairsObject = Object.assign(this.keyValuePairsObject, keyValuePair);
            Utils.updSignal(this.$keyValuePairsArray, x => {
                x.push(keyValuePair);
                return x;
            });
        }
    }

    private getFormFlowObjectEntries(flowObject: FlowObjectDefinition): any[] {
        if (Utils.isNullOrEmpty(flowObject.formSchema)) return;

        let entries = [];
        let formSchema = JSON.parse(flowObject.formSchema) as FormSchema;

        formSchema.components.forEach(item => {
            if (!['button', 'pdfUpload'].includes(item.type)) {
                entries.push({
                    label: item.label,
                    key: `${item.key}`
                });
            }
        });

        return entries;
    }

    private isPairKeyAlreadyAdded(): boolean {
        if (this.keyValuePairsObject.hasOwnProperty(this.$pairKey())) {
            this.toastr.error(Enums.Messages.KeyAlreadyAdded, Enums.Messages.Error, this.getToastrErrorOptions());
            return true;
        }

        return false;
    }

    private isPairKeyEmpty(): boolean {
        if (Utils.isNullOrEmpty(this.$pairKey())) {
            this.toastr.error(Enums.Messages.EmptyKey, Enums.Messages.Error, this.getToastrErrorOptions());
            return true;
        }

        return false;
    }

    private isPairValueTypeEmpty(): boolean {
        if (Utils.isNullOrEmpty(this.$pairValueType())) {
            this.toastr.error(Enums.Messages.EmptyPairValueType, Enums.Messages.Error, this.getToastrErrorOptions());
            return true;
        }

        return false;
    }

    private isPairValueValid(): boolean {
        if (
            Utils.isNotNullOrEmpty(this.$pairValueType())
            && (
                (
                    this.$pairValueType() == this.PairValueTypeValues.String
                    && (Utils.isNotNullOrEmpty(this.$pairValue()))
                ) || (
                    this.$pairValueType() == this.PairValueTypeValues.Number
                    && this.isNumberLike(this.$pairValue())
                ) || (
                    this.$pairValueType() == this.PairValueTypeValues.Boolean
                    && this.isBool(this.$pairValue())
                ) || (
                    this.$pairValueType() == this.PairValueTypeValues.Null
                    && this.isNull(this.$pairValue())
                ) || (
                    this.$pairValueType() == this.PairValueTypeValues.EDocsEnc
                    && this.$pairValue() !== this.PairValueTypeValues.EDocsEnc
                ) || (
                    this.$pairValueType() == this.PairValueTypeValues.EDocsProc
                    && this.$pairValue() !== this.PairValueTypeValues.EDocsProc
                ) || (
                    this.$pairValueType() == this.PairValueTypeValues.SendDateTime
                    && this.$pairValue() !== this.PairValueTypeValues.SendDateTime
                ) || (
                    this.$pairValueType() == this.PairValueTypeValues.AcessoCidadaoIdUsuarioSolicitante
                    && this.$pairValue() !== this.PairValueTypeValues.AcessoCidadaoIdUsuarioSolicitante
                ) || (
                    this.$pairValueType() == this.PairValueTypeValues.AcessoCidadaoIdUsuarioExecutor
                    && this.$pairValue() !== this.PairValueTypeValues.AcessoCidadaoIdUsuarioExecutor
                ) || this.isFormPairValueType()
            )
        ) {
            return true;
        }

        this.toastr.error(Enums.Messages.InvalidPairValue, Enums.Messages.Error, this.getToastrErrorOptions());
        return false;
    }

    private isFormPairValueType(): boolean {
        return this.$pairValueType().startsWith(Enums.Messages.FormTypeStartTag)
            && this.$pairValueType().includes(Enums.Messages.FormTypeSeparatorTag)
            && this.$pairValueType().endsWith(Enums.Messages.FormTypeEndTag);
    }

    private isReallyNumber(value): boolean {
        return typeof value === 'number' && Number.isFinite(value);
    }

    private isNumberLike(value): boolean {
        return !Number.isNaN(Number.parseFloat(value));
    }

    private isBool(value): boolean {
        return value != null
            && (
                typeof value === 'boolean'
                || (
                    typeof value === 'string'
                    && ['true', 'false'].includes(value.toLowerCase())
                )
            );
    }

    private isNull(value): boolean {
        return value === null;
    }

    private parseKeyValuePair(key?, value?): any {
        key = key || this.$pairKey();
        value = value || this.$pairValue();

        try {
            switch (this.$pairValueType()) {
                case this.PairValueTypeValues.String:
                    return JSON.parse(`{"${key}": "${value}"}`);

                case this.PairValueTypeValues.Number:
                    return JSON.parse(`{"${key}": ${value}}`);

                case this.PairValueTypeValues.Boolean:
                    return JSON.parse(`{"${key}": ${value}}`);

                case this.PairValueTypeValues.Null:
                    return JSON.parse(`{"${key}": null}`);

                default:
                    return JSON.parse(`{"${key}" : "${value || this.$pairValueType()}"}`);
            }
        } catch (error) {
            this.toastr.error(Enums.Messages.JsonSerializeError, Enums.Messages.Error, this.getToastrErrorOptions());
            console.error(error);
        }
    }

    private setJsonToEdit() {
        this.keyValuePairsObject = JSON.parse(this.fullJson);
        this.$jsonToEdit.set(JSON.stringify(this.keyValuePairsObject, null, 2));
        let pre = document.createElement('pre');
        pre.innerHTML = this.$jsonToEdit();
        let div = document.createElement('div');
        div.appendChild(pre);
        this.$jsonToEdit.set(div.innerHTML);
    }

    private getJsonToEditText(): string {
        let div = document.createElement('div');
        div.innerHTML = this.$jsonToEdit().replaceAll('&nbsp;', '').replace(/<br *\/*>/g, '');
        return div.innerText.replaceAll('\n', '');
    }

    private getToastrErrorOptions() {
        return {
            closeButton: true,
            timeOut: 10000,
            extendedTimeOut: 5000,
        };
    }

    private isFormValueType(value: any): boolean {
        return value.startsWith(Enums.Messages.FormTypeStartTag)
            && value.includes(Enums.Messages.FormTypeSeparatorTag)
            && value.endsWith(Enums.Messages.FormTypeEndTag);
    }

    private isInvalidTemplate(value: any): boolean {
        if (!value.startsWith('{{') || !value.endsWith('}}')) {
            this.toastr.error(Enums.Messages.InvalidKeyTemplate.toString(), Enums.Messages.Error.toString(), this.getToastrErrorOptions());
            return true;
        }
        return false;
    }

    private isInvalidKey(keyType: any, key: any): boolean {
        if (Utils.isNullOrEmpty(keyType) || Utils.isNullOrEmpty(key)) {
            this.toastr.error(Enums.Messages.NullOrEmpty.toString(), Enums.Messages.Error.toString(), this.getToastrErrorOptions());
            return true;
        }

        if (this.isInvalidTemplate(key)) return true;

        if (keyType == this.PairValueTypeValues.String
            || (
                keyType == this.PairValueTypeValues.Number
                && this.isNumberLike(key)
            ) || (
                keyType == this.PairValueTypeValues.Boolean
                && this.isBool(key)
            ) || (
                keyType == this.PairValueTypeValues.Null
                && this.isNull(key)
            ) || (
                keyType == this.PairValueTypeValues.EDocsEnc
                && key !== this.PairValueTypeValues.EDocsEnc
            ) || (
                keyType == this.PairValueTypeValues.EDocsProc
                && key !== this.PairValueTypeValues.EDocsProc
            ) || (
                keyType == this.PairValueTypeValues.SendDateTime
                && key !== this.PairValueTypeValues.SendDateTime
            ) || (
                keyType == this.PairValueTypeValues.AcessoCidadaoCpf
                && key !== this.PairValueTypeValues.AcessoCidadaoCpf
            ) || (
                keyType == this.PairValueTypeValues.AcessoCidadaoIdUsuarioSolicitante
                && key !== this.PairValueTypeValues.AcessoCidadaoIdUsuarioSolicitante
            ) || (
                keyType == this.PairValueTypeValues.AcessoCidadaoIdUsuarioExecutor
                && key !== this.PairValueTypeValues.AcessoCidadaoIdUsuarioExecutor
            ) || this.isFormValueType(keyType)
        ) {
            return false;
        }

        this.toastr.error(Enums.Messages.InvalidPairValue.toString(), Enums.Messages.Error.toString(), this.getToastrErrorOptions());
        return true;
    }
}
